import * as React from 'react';
import * as moment from 'moment';
import { useState } from 'react';
import { FormControl, ListGroup, ListGroupItem, Panel, Well } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import { IDataComplianceEntityGroup } from '@nkr/models/index';
import useDataComplianceIssues from '@nkr/store/center/hooks/use-data-compliance-issues';
import { styles } from './styles';

interface IProps {
    centerId: number
}

const stripHtml = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const getInitialMonth = (): string => {
    const params = new URLSearchParams(window.location.search);
    return params.get('month') || moment().subtract(1, 'month').format('YYYY-MM');
};

const renderEntityList = (items: IDataComplianceEntityGroup[]) => (
    <ListGroup style={styles.flushListGroup}>
        {items.map((item, i) => (
            <ListGroupItem key={i}>
                {i + 1}. <strong>{item.displayName}</strong> is missing the following information:
                <ul style={styles.issueList}>
                    {item.issues.map((issue, j) => <li key={j}>{stripHtml(issue)}</li>)}
                </ul>
            </ListGroupItem>
        ))}
    </ListGroup>
);

const renderSubGroup = (label: string, items: IDataComplianceEntityGroup[]) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <Panel>
            <Panel.Heading style={styles.subGroupHeading}>{label}</Panel.Heading>
            {renderEntityList(items)}
        </Panel>
    );
};

const DataComplianceIssuesReport = (props: IProps) => {
    const [month, setMonth] = useState(getInitialMonth);
    const { results, loading, error } = useDataComplianceIssues(props.centerId, month);

    const dateRange = Utility.getRange(moment().subtract(11, 'month'), moment().add(1, 'month'), 'month');

    const handleMonthChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        setMonth(e.currentTarget.value);
    };

    const activeRecipients   = results ? results.recipients.filter(x => x.category === 1) : [];
    const activeDonors       = results ? results.donors.filter(x => x.category === 1)     : [];
    const rdnDonors          = results ? results.donors.filter(x => x.category === 2)     : [];
    const postTxpRecipients  = results ? results.recipients.filter(x => x.category === 3) : [];
    const postTxpDonors      = results ? results.donors.filter(x => x.category === 3)     : [];
    const postTxpRdnDonors   = results ? results.donors.filter(x => x.category === 4)     : [];

    const hasActive  = activeRecipients.length > 0 || activeDonors.length > 0 || rdnDonors.length > 0;
    const hasPostTxp = postTxpRecipients.length > 0 || postTxpDonors.length > 0 || postTxpRdnDonors.length > 0;
    const hasIssues  = results != null && (
        results.centerIssues.length > 0 ||
        results.donors.length > 0 ||
        results.recipients.length > 0 ||
        results.dashRemoteStatus.length > 0 ||
        results.microsite.length > 0
    );

    return (
        <>
            <Well bsSize='sm' style={styles.monthPickerWell}>
                <div style={styles.monthPickerInner}>
                    <div style={styles.monthPickerLabel}>Month:</div>
                    <FormControl
                        componentClass='select'
                        bsSize='sm'
                        value={month}
                        onChange={handleMonthChange}>
                        {dateRange.map(date => (
                            <option key={date.format('YYYY-MM')} value={date.format('YYYY-MM')}>
                                {date.format('MM/YYYY')}
                            </option>
                        ))}
                    </FormControl>
                </div>
            </Well>

            {loading && (
                <p>Loading...</p>
            )}

            {!loading && error && (
                <p>{error}</p>
            )}

            {!loading && !error && results == null && (
                <p>No snapshot was recorded for this month.</p>
            )}

            {!loading && !error && results != null && !hasIssues && (
                <p>
                    No compliance issues were found for this month. This may be because there were none,
                    or because detailed issue tracking was not yet in place for this period.
                </p>
            )}

            {!loading && !error && hasIssues && results.overallFailed === 0 && (
                <p style={styles.summary}>
                    A total of {results.overallChecked} data compliance items were examined and there
                    were no exceptions found. This gives a Data Compliance score of {results.overallPercent.toFixed(1)}%
                </p>
            )}

            {!loading && !error && hasIssues && results.overallFailed === 1 && (
                <p style={styles.summary}>
                    A total of {results.overallChecked} data compliance items were examined and
                    the {results.overallFailed} item listed below was an exception. This gives a Data Compliance score
                    of {results.overallPercent.toFixed(1)}%
                </p>
            )}

            {!loading && !error && hasIssues && results.overallFailed > 1 && (
                <p style={styles.summary}>
                    A total of {results.overallChecked} data compliance items were examined and
                    the {results.overallFailed} items listed below were exceptions. This gives a Data Compliance score
                    of {results.overallPercent.toFixed(1)}%
                </p>
            )}

            {!loading && !error && hasIssues && (
                <>
                    {results.centerIssues.length > 0 && (
                        <div style={styles.centerIssues}>
                            <p style={styles.centerIssuesTitle}>
                                <strong>Your center is missing important member center profile information:</strong>
                            </p>
                            <ul>
                                {results.centerIssues.map((issue, i) => <li key={i}>{stripHtml(issue)}</li>)}
                            </ul>
                        </div>
                    )}

                    {results.dashRemoteStatus.length > 0 && (
                        <Panel style={styles.sectionPanel}>
                            <Panel.Heading>
                                <Panel.Title>The following DASH Referrals require updates</Panel.Title>
                            </Panel.Heading>
                            {renderEntityList(results.dashRemoteStatus)}
                        </Panel>
                    )}

                    {hasActive && (
                        <Panel style={styles.sectionPanel}>
                            <Panel.Heading>
                                <Panel.Title>
                                    The following Recipients/Donors are missing important information
                                    which will reduce their chances of a match
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {renderSubGroup('Recipients', activeRecipients)}
                                {renderSubGroup('Donors', activeDonors)}
                                {renderSubGroup('RDN Donors', rdnDonors)}
                            </Panel.Body>
                        </Panel>
                    )}

                    {hasPostTxp && (
                        <Panel style={styles.sectionPanel}>
                            <Panel.Heading>
                                <Panel.Title>
                                    The following Recipients/Donors are missing important post transplant information
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {renderSubGroup('Post Transplant Recipients', postTxpRecipients)}
                                {renderSubGroup('Post Transplant Donors', postTxpDonors)}
                                {renderSubGroup('Post Transplant RDN Donors', postTxpRdnDonors)}
                            </Panel.Body>
                        </Panel>
                    )}

                    {results.microsite.length > 0 && (
                        <Panel style={styles.sectionPanel}>
                            <Panel.Heading>
                                <Panel.Title>The following Microsite Recipients are missing required data</Panel.Title>
                            </Panel.Heading>
                            {renderEntityList(results.microsite)}
                        </Panel>
                    )}
                </>
            )}
        </>
    );
};

export default DataComplianceIssuesReport;
